<!--支持showPopover-->
<template>
  <component
    :is="showCloseModal ? ElOverlay : 'div'"
    overlay-class="easy-popover-overlay"
    :mask="true"
    :z-index="zIndex"
    @click.self="hide"
  >
    <el-popover
      ref="popoverRef"
      :virtual-ref="virtualRef"
      virtual-triggering
      visible
      :placement="placement"
      :fallback-placements="['bottom', 'top', 'right', 'left']"
      :width="width"
      :teleported="false"
      :popper-style="popperStyle"
      popper-class="easy-popover easy-popup"
      @after-leave="popupCtx.done!(undefined)"
      @blur.self="onBlur"
    >
      <component :is="defaultSlot" />

      <!--footer slot-->
      <div v-if="footerSlot" class="footer-cont">
        <component :is="footerSlot" />
      </div>

      <div v-else-if="defaultFooter" class="footer-cont">
        <span class="footer-space" />
        <el-button type="primary" @click="popupCtx.confirm?.()">确定</el-button>
      </div>
    </el-popover>
  </component>
</template>
<script lang="ts" setup>
import { ref, computed, Slot, Ref, StyleValue, provide } from 'vue';
import { useZIndex, ElOverlay } from 'element-plus';
import {
  useElementSize,
  useMutationObserver,
  watchDebounced,
  onClickOutside
} from '@vueuse/core';

const props = defineProps<{
  virtualRef: any;

  placement:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'auto'
    | 'auto-start'
    | 'auto-end';

  width: string | number;

  popperStyle: StyleValue | undefined;

  showCloseModal: boolean; // 是否显示遮罩层

  closeOnBlur: boolean; // 失去焦点时自动关闭

  defaultSlot: Slot;

  defaultFooter: boolean; // 显示默认"确定""取消", 优化级低于footerSlot
  footerSlot: () => Ref<Slot | undefined>;

  popupCtx: {
    done: (r?: any) => void;

    confirm?: () => void;
  };
}>();

const popoverRef = ref();

const footerSlot = props.footerSlot();

const { nextZIndex } = useZIndex();
const zIndex = nextZIndex();

// 监控大小变化, 调整位置
const contentRef = computed(() => popoverRef.value?.popperRef.contentRef);
const size = useElementSize(contentRef);
watchDebounced(
  () => [size.width.value, size.height.value],
  () => {
    popoverRef.value?.popperRef.popperInstanceRef.forceUpdate();
  },
  { debounce: 100, maxWait: 500 }
);

// visibility变hidden时, 关闭popper
useMutationObserver(
  contentRef,
  (mutations) => {
    mutations.forEach((m) => {
      if (m.type == 'attributes' && m.attributeName == 'style') {
        const style = window.getComputedStyle(m.target as any);
        if (style && style.visibility == 'hidden') {
          hide();
        }
      }
    });
  },
  { attributes: true, attributeFilter: ['style', 'class'] }
);

//
let hidden = false;
function hide() {
  if (hidden) return;

  popoverRef.value?.hide?.();
  hidden = true;
}

// 点击外部关闭
if (!props.showCloseModal) {
  onClickOutside(contentRef, hide);
}

// 失去焦点关闭
function onBlur() {
  if (props.closeOnBlur) hide();
}

// 提供给PopupCloseButton
provide('PopupContainer', () => props.popupCtx.done());

defineExpose({
  // 暴露给EasyImage
  hide
});
</script>

<style lang="scss" scoped>
.easy-popover-overlay {
  background-color: transparent !important;
}

.footer-cont {
  display: flex;
  margin-top: 8px;

  :deep(.footer-space) {
    flex-grow: 1;
  }
}
</style>
